import { useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

const CONTROLS =
  'a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,[tabindex]:not([tabindex="-1"])';

/**
 * Makes a `role="dialog"` element behave as a modal: it takes focus when it mounts, Tab and
 * Shift+Tab wrap inside it, Escape closes it, and whatever had focus before gets it back when it
 * unmounts. Give the element `aria-modal="true"`, `tabIndex={-1}`, `ref` and `onKeyDown`.
 */
export function useModalDialog(
  onClose: () => void,
): [RefObject<HTMLDivElement | null>, (e: KeyboardEvent<HTMLDivElement>) => void] {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => {
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    // Skip controls that are hidden; when nothing is laid out (jsdom) keep them all.
    const laidOut = ref.current.getClientRects().length > 0;
    const visible = Array.from(ref.current.querySelectorAll<HTMLElement>(CONTROLS)).filter(
      (el) => !laidOut || el.getClientRects().length > 0,
    );
    const first = visible[0];
    const last = visible.at(-1);
    if (!first || !last) return;
    if (
      e.shiftKey &&
      (document.activeElement === first || document.activeElement === ref.current)
    ) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  return [ref, onKeyDown];
}
